// src/whatsappChat/api/hooks.js
import { useCallback, useEffect, useMemo, useState } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { waChatClient } from './client';
import { markConversationRead, mergeMessages, patchThread } from './cache';
import { useWaSocket } from '../socket/WaSocketProvider';
import { hasPermission } from '../../helpers/permission';

/** CRM role key (HRMS > Role) that grants the Templates section. */
const TEMPLATES_ROLE_KEY = 'whatsapp_chat_templates';

export const NO_PERMISSIONS = {
  menu: false,
  inbox: { view: false, send: false, scope: 'none', unknown_numbers: false },
  templates: { view: false, create: false },
  setup: { view: false },
};

/**
 * Rights for the WhatsApp Chat module. Fetched once, refreshed when the CRM
 * role details are reloaded (`permissionsUpdated` window event).
 */
export function useWaPermissions() {
  const qc = useQueryClient();

  useEffect(() => {
    const refresh = () => qc.invalidateQueries({ queryKey: ['wa-permissions'] });
    window.addEventListener('permissionsUpdated', refresh);
    return () => window.removeEventListener('permissionsUpdated', refresh);
  }, [qc]);

  const query = useQuery({
    queryKey: ['wa-permissions'],
    queryFn: async () => {
      const res = await waChatClient.get('/permissions');
      const data = res.data?.data || NO_PERMISSIONS;
      // Interim: /permissions derives templates from the inbox rights, so read
      // them from the CRM role key until the backend maps it.
      return {
        ...data,
        templates: {
          view: hasPermission(TEMPLATES_ROLE_KEY, 'view'),
          create: hasPermission(TEMPLATES_ROLE_KEY, 'add'),
        },
      };
    },
    staleTime: Infinity,
    retry: 1,
  });

  return { ...query, permissions: query.data || NO_PERMISSIONS };
}

export function useWaNumbers(enabled = true) {
  return useQuery({
    queryKey: ['wa-numbers'],
    queryFn: async () => {
      const res = await waChatClient.get('/numbers');
      return res.data?.data || [];
    },
    enabled,
    staleTime: 60 * 1000,
  });
}

export function useWaSetupCheck(enabled = true) {
  return useQuery({
    queryKey: ['wa-setup-check'],
    queryFn: async () => {
      const res = await waChatClient.get('/setup-check');
      return res.data?.data || [];
    },
    enabled,
    refetchOnWindowFocus: false,
    retry: 0,
  });
}

// ---------------------------------------------------------------------------
// Inbox
// ---------------------------------------------------------------------------

const CONVERSATIONS_PAGE = 30;
const MESSAGES_PAGE = 50;
// The socket pushes updates, but the inbox and the open chat are also polled
// while the tab is visible, so nothing is missed when a push does not arrive.
const LIST_POLL_MS = 10000;
const THREAD_POLL_MS = 10000;

export function useWaConversations({ search = '', filter = 'all', numberId = '' }, { enabled = true } = {}) {
  const query = useInfiniteQuery({
    queryKey: ['wa-conversations', { search, filter, numberId }],
    enabled,
    initialPageParam: 0,
    queryFn: async ({ pageParam }) => {
      const res = await waChatClient.get('/conversations', {
        params: {
          limit: CONVERSATIONS_PAGE,
          offset: pageParam,
          filter,
          ...(search ? { search } : {}),
          ...(numberId ? { number_id: numberId } : {}),
        },
      });
      return res.data;
    },
    getNextPageParam: (lastPage, pages) =>
      lastPage?.has_more ? pages.reduce((n, p) => n + (p?.data?.length || 0), 0) : undefined,
    // Pauses by itself while the tab is in the background.
    refetchInterval: LIST_POLL_MS,
    refetchOnWindowFocus: true,
    // Re-render only when the rows (or an error / paging state) change, not on every poll.
    notifyOnChangeProps: ['data', 'error', 'status', 'hasNextPage', 'isFetchingNextPage'],
    placeholderData: keepPreviousData,
  });

  // Offset paging over a moving list can repeat a row; keep the first sighting.
  const conversations = useMemo(() => {
    const seen = new Set();
    const rows = [];
    (query.data?.pages || []).forEach((page) => {
      (page?.data || []).forEach((row) => {
        if (!seen.has(row.id)) {
          seen.add(row.id);
          rows.push(row);
        }
      });
    });
    return rows;
  }, [query.data]);

  return { ...query, conversations };
}

/** Number of chats with unread messages, for the menu badge. `more` = there are further pages. */
export function useWaUnreadChats(enabled = true) {
  const { conversations, hasNextPage } = useWaConversations({ filter: 'unread' }, { enabled });
  const count = useMemo(() => conversations.filter((row) => row.unread_count > 0).length, [conversations]);
  return { count, more: Boolean(hasNextPage) };
}

/**
 * One open chat: first page, "load older", and live updates.
 * Live = `chat:open` on the socket. A poll every 10 seconds runs alongside it
 * while the tab is visible.
 * The thread lives in the query cache under ['wa-thread', id] (see cache.js).
 */
export function useWaThread(conversationId) {
  const qc = useQueryClient();
  const { connected, emit } = useWaSocket();
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [live, setLive] = useState(false);
  const id = conversationId ? Number(conversationId) : null;

  const query = useQuery({
    queryKey: ['wa-thread', id],
    enabled: Boolean(id),
    queryFn: async () => {
      const res = await waChatClient.get('/messages', {
        params: { conversation_id: id, limit: MESSAGES_PAGE },
      });
      const body = res.data || {};
      return {
        messages: mergeMessages([], body.data || []),
        hasMoreOlder: Boolean(body.has_more),
        windowOpen: Boolean(body.window_open),
        conversation: body.conversation || null,
        subscriber: body.subscriber || null,
        accessLost: false,
      };
    },
    // Refetching would drop older pages already loaded; new rows are merged in instead.
    staleTime: Infinity,
    gcTime: 0,
    refetchOnWindowFocus: false,
    retry: 0,
  });

  const loaded = query.isSuccess;
  const messages = query.data?.messages;

  const loadOlder = useCallback(async () => {
    const current = qc.getQueryData(['wa-thread', id]);
    const oldest = current?.messages?.[0]?.id;
    if (!oldest || !current.hasMoreOlder) return;
    setLoadingOlder(true);
    try {
      const res = await waChatClient.get('/messages', {
        params: { conversation_id: id, limit: MESSAGES_PAGE, before_id: oldest },
      });
      patchThread(qc, id, (prev) => ({
        messages: mergeMessages(prev.messages, res.data?.data || []),
        hasMoreOlder: Boolean(res.data?.has_more),
      }));
    } finally {
      setLoadingOlder(false);
    }
  }, [id, qc]);

  // Mark read on open and again whenever another customer message shows up.
  const lastInboundId = useMemo(() => {
    for (let i = (messages?.length || 0) - 1; i >= 0; i -= 1) {
      if (messages[i].direction === 'inbound') return messages[i].id;
    }
    return 0;
  }, [messages]);

  useEffect(() => {
    if (loaded) markConversationRead(qc, id);
  }, [loaded, id, lastInboundId, qc]);

  // Live updates. Re-runs after every reconnect, which re-sends chat:open with last_id.
  useEffect(() => {
    if (!loaded || !id || !connected) return undefined;
    let active = true;
    const current = qc.getQueryData(['wa-thread', id])?.messages || [];
    const lastId = current.length ? current[current.length - 1].id : undefined;

    emit('chat:open', { conversation_id: id, ...(lastId ? { last_id: lastId } : {}) }, (ack) => {
      if (active) setLive(Boolean(ack?.success));
    });

    return () => {
      active = false;
      setLive(false);
      emit('chat:close', { conversation_id: id });
    };
  }, [loaded, id, connected, emit, qc]);

  // Pull anything newer than what is shown, over REST.
  const fetchNew = useCallback(async () => {
    const current = qc.getQueryData(['wa-thread', id]);
    if (!current) return;
    const lastId = current.messages.length ? current.messages[current.messages.length - 1].id : 0;
    // A status can still change after the first load. With no socket to push it,
    // re-read the latest page (not just the rows after last_id) while any is unsettled.
    const unsettled = current.messages.some(
      (m) => m.direction === 'outbound' && m.status !== 'read' && m.status !== 'failed'
    );
    try {
      const res = await waChatClient.get('/messages', {
        params: { conversation_id: id, limit: MESSAGES_PAGE, ...(unsettled ? {} : { after_id: lastId }) },
      });
      const fresh = res.data?.data || [];
      patchThread(qc, id, (prev) => ({
        messages: fresh.length ? mergeMessages(prev.messages, fresh) : prev.messages,
        windowOpen: typeof res.data?.window_open === 'boolean' ? res.data.window_open : prev.windowOpen,
      }));
    } catch {
      // Next tick tries again.
    }
  }, [id, qc]);

  // Poll while the chat is open and the tab is visible, live or not.
  useEffect(() => {
    if (!loaded || !id) return undefined;
    const tick = () => {
      if (!document.hidden) fetchNew();
    };
    const timer = setInterval(tick, THREAD_POLL_MS);
    document.addEventListener('visibilitychange', tick);
    return () => {
      clearInterval(timer);
      document.removeEventListener('visibilitychange', tick);
    };
  }, [loaded, id, fetchNew]);

  return { ...query, thread: query.data || null, loadOlder, loadingOlder, live, fetchNew };
}

const approvedTemplatesQuery = (numberId, enabled) => ({
  queryKey: ['wa-templates-approved', numberId],
  enabled: Boolean(numberId) && enabled,
  queryFn: async () => {
    const res = await waChatClient.get('/templates', {
      params: { number_id: numberId, status: 'approved', limit: 100 },
    });
    return res.data?.data || [];
  },
  staleTime: 5 * 60 * 1000,
  retry: 0,
});

/** Approved templates of one business number, for the picker. */
export function useWaApprovedTemplates(numberId, enabled = true) {
  return useQuery(approvedTemplatesQuery(numberId, enabled));
}

// ---------------------------------------------------------------------------
// Sending
// ---------------------------------------------------------------------------

/**
 * POST /messages. Never retried automatically: BotSense cannot de-duplicate,
 * so a retry after a timeout could deliver the message twice.
 */
export function useWaSendMessage() {
  return useMutation({
    mutationFn: async (payload) => {
      const res = await waChatClient.post('/messages', payload);
      return res.data?.data || null;
    },
    retry: 0,
  });
}

/** POST /media. Returns { id, url }: `id` goes out as media_id, `url` (may be null) as media_link. */
export async function uploadWaMedia(file, numberId) {
  const form = new FormData();
  form.append('file', file);
  const res = await waChatClient.post('/media', form, {
    params: numberId ? { number_id: numberId } : {},
  });
  return { id: res.data?.id, url: res.data?.url || null };
}

/** Existing chat for this phone on this business number, if any. */
export async function findWaConversation(phoneDigits, numberId) {
  const res = await waChatClient.get('/conversations', {
    params: { search: phoneDigits, number_id: numberId, limit: 20 },
  });
  const tail = phoneDigits.slice(-10);
  return (
    (res.data?.data || []).find(
      (row) => row.number_id === numberId && String(row.phone || '').endsWith(tail)
    ) || null
  );
}

/** Get current user's prompt and system prompt */
export function useWaPrompts() {
  return useQuery({
    queryKey: ['wa-prompts'],
    queryFn: async () => {
      const res = await waChatClient.get('/prompts');
      return res.data?.data || { user_prompt: '', system_prompt: '' };
    },
    staleTime: 60 * 1000,
  });
}

/** Save current user's prompt or system prompt */
export function useWaSavePrompts() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await waChatClient.post('/prompts', payload);
      return res.data?.data || null;
    },
    onSuccess: (data) => {
      if (data) {
        qc.setQueryData(['wa-prompts'], data);
      } else {
        qc.invalidateQueries({ queryKey: ['wa-prompts'] });
      }
    },
  });
}

/** Generate 3 AI suggestions using DeepSeek and latest 20 messages */
export function useWaChatSuggestions() {
  return useMutation({
    mutationFn: async (conversationId) => {
      const res = await waChatClient.post('/suggestions', {
        conversation_id: Number(conversationId),
      });
      return res.data?.data?.suggestions || [];
    },
    retry: 0,
  });
}

/** Fetch custom templates from tbl_botsense_templates */
export function useWaCustomTemplates() {
  return useQuery({
    queryKey: ['wa-custom-templates'],
    queryFn: async () => {
      const res = await waChatClient.get('/custom-templates');
      return Array.isArray(res.data?.data) ? res.data.data : [];
    },
    staleTime: 30 * 1000,
  });
}

/** Create or update a custom template in tbl_botsense_templates */
export function useWaSaveCustomTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload) => {
      const res = await waChatClient.post('/custom-templates', payload);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-custom-templates'] });
    },
  });
}

/** Delete a custom template from tbl_botsense_templates */
export function useWaDeleteCustomTemplate() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id) => {
      const res = await waChatClient.delete(`/custom-templates/${id}`);
      return res.data;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['wa-custom-templates'] });
    },
  });
}


